import React, { useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'

import type {
	FieldSpec,
	FormSpecData,
} from '@/api/repository/repositorySchemas'
import { AstaTableV2 } from '@/components/shared/AstaTable/AstaTable'

type Props = {
	spec: FormSpecData | null
}

type ColumnContainerProps = {
	start?: boolean
}

const ColumnContainer: React.FC<
	React.PropsWithChildren<ColumnContainerProps>
> = ({ start, children }) => (
	<div
		className={`w-full h-[26px] flex items-center ${start ? 'justify-start' : 'justify-center'}`}
	>
		{children}
	</div>
)

const columnHelper = createColumnHelper<FieldSpec>()

const FormSpecEditor: React.FC<Props> = ({ spec }) => {
	const defaultColumns = useMemo(
		() => [
			columnHelper.accessor(row => row.fieldNumber, {
				id: 'fieldNumber',
				header: 'Field Number',
				minSize: 120,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.fieldLabel, {
				id: 'fieldLabel',
				header: 'Field Label',
				minSize: 150,
				cell: props => (
					<ColumnContainer start>
						<span className="max-w-[400px] truncate">
							{props.renderValue()}
						</span>
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.fieldId, {
				id: 'fieldId',
				header: 'Field ID',
				minSize: 80,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.required, {
				id: 'required',
				header: 'Required',
				minSize: 80,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.minimumOccurrences, {
				id: 'minimumOccurrences',
				header: 'Minimum Occurrences',
				minSize: 140,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.maximumOccurrences, {
				id: 'maximumOccurrences',
				header: 'Maximum Occurrences',
				minSize: 140,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.agencyFieldName, {
				id: 'agencyFieldName',
				header: 'Agency Field Name',
				minSize: 140,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.fieldType, {
				id: 'fieldType',
				header: 'Field Type',
				minSize: 100,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.globalLibraryFieldName, {
				id: 'globalLibraryFieldName',
				header: 'Global Library Field Name',
				minSize: 160,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.fieldTypeSource, {
				id: 'fieldTypeSource',
				header: 'Field Type Source',
				minSize: 130,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.businessRules, {
				id: 'businessRules',
				header: 'Business Rules',
				minSize: 120,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.dataType, {
				id: 'dataType',
				header: 'Data Type',
				minSize: 90,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.listOfValues, {
				id: 'listOfValues',
				header: 'List of Values',
				minSize: 120,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.minCharsOrMinValue, {
				id: 'minCharsOrMinValue',
				header: 'Minimum Chars/Value',
				minSize: 150,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.maxCharsOrMaxValue, {
				id: 'maxCharsOrMaxValue',
				header: 'Maximum Chars/Value',
				minSize: 150,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.fieldImplementation, {
				id: 'fieldImplementation',
				header: 'Field Implementation',
				minSize: 150,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.helpTip, {
				id: 'helpTip',
				header: 'Help Tip',
				minSize: 90,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.mandatoryMessage, {
				id: 'mandatoryMessage',
				header: 'Mandatory Message',
				minSize: 140,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
			columnHelper.accessor(row => row.validationMessage, {
				id: 'validationMessage',
				header: 'Validation Message',
				minSize: 140,
				cell: props => (
					<ColumnContainer start>
						{props.renderValue()}
					</ColumnContainer>
				),
			}),
		],
		[]
	)

	const data = useMemo(
		() =>
			spec
				? [
						{
							fieldNumber: 'Form Title',
							fieldLabel: spec.formTitle,
						},
						{ fieldNumber: 'Form Id', fieldLabel: spec.formId },
						{
							fieldNumber: 'Form Version',
							fieldLabel: spec.formVersion,
						},
						{
							fieldNumber: 'OMB Control Number',
							fieldLabel: spec.ombControlNumber,
						},
						{
							fieldNumber: 'OMB Expiration Date',
							fieldLabel: spec.ombExpirationDate,
						},
						{
							fieldNumber: 'Form Families',
							fieldLabel: spec.formFamilies?.join(', '),
						},
						...spec.fields,
					]
				: [],
		[spec]
	)

	return <AstaTableV2 columns={defaultColumns} data={data} actions={[]} />
}

export default FormSpecEditor
